import {
  ReminderDefEmailSummaryDto,
  ReminderDefScheduleDto,
  ReminderDefSmsSummaryDto,
  ReminderDefSummaryDto,
} from '@savvy/reminders';

/**
 * Mirrors the published v3 schedule contract while this checkout waits for the
 * generated @savvy/reminders@14.0.44 declaration to arrive in the registry.
 */
export interface ReminderDefV3Schedule extends Omit<ReminderDefScheduleDto, 'daysSchedule'> {
  daysSchedule?: number | null;
  timeToSend?: string | null;
}

export interface ReminderDefV3EmailConfig extends Omit<ReminderDefEmailSummaryDto, 'schedules'> {
  schedules?: Array<ReminderDefV3Schedule>;
}

export interface ReminderDefV3SmsConfig extends Omit<ReminderDefSmsSummaryDto, 'schedules'> {
  schedules?: Array<ReminderDefV3Schedule>;
}

export interface ReminderDefV3WhatsAppConfig {
  enabled: boolean;
}

export interface ReminderDefV3Summary extends Omit<ReminderDefSummaryDto, 'emailConfig' | 'smsConfig'> {
  emailConfig?: ReminderDefV3EmailConfig;
  smsConfig?: ReminderDefV3SmsConfig;
  /** v3 per-channel WhatsApp flag. Not in the SDK type yet; passed through untouched so saves never drop it. */
  whatsAppConfig?: ReminderDefV3WhatsAppConfig | null;
}

const scheduleTypes: ReminderDefScheduleDto.ScheduleTypeEnum[] = [
  ReminderDefScheduleDto.ScheduleTypeEnum.DaysBefore,
  ReminderDefScheduleDto.ScheduleTypeEnum.DaysAfter,
  ReminderDefScheduleDto.ScheduleTypeEnum.OnSameDayOfYear,
  ReminderDefScheduleDto.ScheduleTypeEnum.Immediate,
];

/**
 * Rebuilds a reminder at the v3 API boundary so it contains only canonical
 * schedule fields and no legacy nested schedule structure.
 */
export function toReminderDefV3Payload(reminderDef: ReminderDefV3Summary): ReminderDefV3Summary {
  return {
    id: reminderDef.id,
    type: reminderDef.type,
    name: reminderDef.name,
    enabled: reminderDef.enabled,
    emailConfig: mapEmailConfig(reminderDef.emailConfig),
    smsConfig: mapSmsConfig(reminderDef.smsConfig),
    ...(hasWhatsAppConfig(reminderDef) ? { whatsAppConfig: (reminderDef as ReminderDefV3Summary).whatsAppConfig } : {}),
  };
}

/**
 * Normalizes a v3 response before it becomes editable client state.
 */
export function fromReminderDefV3Response(reminderDef: ReminderDefSummaryDto | ReminderDefV3Summary): ReminderDefV3Summary {
  return toReminderDefV3Payload(reminderDef);
}

function hasWhatsAppConfig(reminderDef: ReminderDefV3Summary | ReminderDefSummaryDto): boolean {
  return !!(reminderDef as ReminderDefV3Summary).whatsAppConfig;
}

function mapEmailConfig(config: ReminderDefV3EmailConfig | ReminderDefEmailSummaryDto | undefined): ReminderDefV3EmailConfig | undefined {
  if (!config) {
    return undefined;
  }

  return {
    enabled: config.enabled,
    ...(config.body === undefined ? {} : { body: config.body }),
    ...(config.subject === undefined ? {} : { subject: config.subject }),
    ...(config.links === undefined ? {} : { links: config.links }),
    schedules: mapSchedules(config.schedules),
  };
}

function mapSmsConfig(config: ReminderDefV3SmsConfig | ReminderDefSmsSummaryDto | undefined): ReminderDefV3SmsConfig | undefined {
  if (!config) {
    return undefined;
  }

  return {
    enabled: config.enabled,
    ...(config.text === undefined ? {} : { text: config.text }),
    ...(config.links === undefined ? {} : { links: config.links }),
    schedules: mapSchedules(config.schedules),
  };
}

function mapSchedules(schedules: Array<ReminderDefV3Schedule | ReminderDefScheduleDto> | undefined): Array<ReminderDefV3Schedule> {
  return (schedules || [])
    .filter(schedule => schedule && isV3ScheduleType(schedule.scheduleType))
    .map(schedule => {
      const v3Schedule = schedule as ReminderDefV3Schedule;
      return schedule.scheduleType === ReminderDefScheduleDto.ScheduleTypeEnum.Immediate
      ? { scheduleType: schedule.scheduleType, daysSchedule: null, timeToSend: null }
      : {
        scheduleType: schedule.scheduleType,
        daysSchedule: schedule.daysSchedule,
        timeToSend: normalizeTimeToSend(v3Schedule.timeToSend),
      };
    });
}

function normalizeTimeToSend(timeToSend: string | null | undefined): string | null {
  if (typeof timeToSend !== 'string') {
    return null;
  }

  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(timeToSend);
  return match ? `${match[1]}:${match[2]}:${match[3] ?? '00'}` : null;
}

function isV3ScheduleType(
  scheduleType: ReminderDefScheduleDto.ScheduleTypeEnum | undefined,
): boolean {
  return scheduleTypes.includes(scheduleType as ReminderDefScheduleDto.ScheduleTypeEnum)
    && scheduleType !== ReminderDefScheduleDto.ScheduleTypeEnum.Unknown;
}
